import { z } from 'zod';
import type { QueryClient } from '@tanstack/react-query';
import type { WebSocketEventRouter } from '@shared/websocket/router';
import type { WebSocketEnvelope } from '@shared/websocket/types';
import { useIncomingCallStore } from '../store/incomingCallStore';

// Схема для уведомления о входящем звонке
// Backend отправляет данные в event.data с полями: title, message, type, asterisk_id, phone_number, customer_id
const IncomingCallNotificationSchema = z.object({
  title: z.string().optional(),
  message: z.string(),
  type: z.string().optional(),
  asterisk_id: z.string().optional(),
  phone_number: z.string().optional(),
  customer_id: z.number().nullable().optional(),
  caller_id_name: z.string().optional(),
});

export interface CallEventsDependencies {
  queryClient: QueryClient;
}

/**
 * Регистрирует обработчики WebSocket событий для входящих звонков
 */
export function registerCallWebSocketHandlers(
  router: WebSocketEventRouter,
  { queryClient }: CallEventsDependencies,
): () => void {
  const handleIncomingCallNotification = (event: WebSocketEnvelope) => {
    // Проверяем, что это уведомление о входящем звонке
    // В backend мы отправляем через Publish с type="notification"
    const result = IncomingCallNotificationSchema.safeParse(event.data ?? {});
    if (!result.success) {
      return;
    }

    const { title, message, asterisk_id, phone_number, customer_id, caller_id_name } = result.data;

    // Проверяем, что это уведомление о звонке (по заголовку или содержимому сообщения)
    const isIncomingCall = 
      title === 'Входящий звонок' || 
      (message && (message.includes('ВХОДЯЩИЙ ЗВОНОК') || message.includes('Входящий звонок')));
    
    if (!isIncomingCall) {
      return; // Не уведомление о звонке
    }

    // Извлекаем данные
    const asteriskId = asterisk_id || '';
    const phoneNumber = phone_number || '';
    const customerId = customer_id ?? null;

    // Создаем уведомление
    const notification: import('../store/incomingCallStore').IncomingCallNotification = {
      id: `${asteriskId}-${Date.now()}`, // Уникальный ID
      notificationId: 0, // Будет обновлен после получения из БД
      phoneNumber,
      asteriskId,
      customerId,
      htmlMessage: message || '',
      timestamp: Date.now(),
      callerIdName: caller_id_name,
    };

    // Добавляем в store
    useIncomingCallStore.getState().addNotification(notification);

    // Инвалидируем список уведомлений (если нужно)
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  // Регистрируем обработчик для событий типа "notification"
  // Проверяем содержимое, чтобы отфильтровать только звонки
  const dispose = router.on('notification', handleIncomingCallNotification);

  return () => {
    dispose();
  };
}

